import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

// Add this type definition
type Position = 'SB' | 'BB' | 'UTG' | 'UTG+1' | 'MP' | 'LJ' | 'HJ' | 'CO' | 'BTN';

interface PlayerPosition {
  position: THREE.Vector3;
  tablePosition: Position;
  name: string;
  displayName: string;
  cards: THREE.Group[];
  chipStack: THREE.Group;
  isActive: boolean;
}

interface PokerAction {
  player: string;
  type: 'raise' | 'call' | 'fold';
  amount?: number;
}

interface CardData {
  rank: string;
  suit: string;
  mesh: THREE.Group;
  targetPosition: THREE.Vector3;
  targetRotation: THREE.Euler;
}

interface CommunityCards {
  flop: CardData[];
  turn: CardData | null;
  river: CardData | null;
}

interface ActionText {
  mesh: THREE.Sprite;
  startTime: number;
  duration: number;
}

interface SimulatorPageProps {}

interface ParsedHand {
  heroCards: string[];
  board: string[];
  actions: {
    street: 'preflop' | 'flop' | 'turn' | 'river';
    player: string;
    position: string;
    type: string;
    amount?: number;
    actionNumber: number;
  }[];
  players: {
    name: string;
    position: string;
  }[];
  pot: number;
}

const CodexPage: React.FC<SimulatorPageProps> = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const capturer = useRef<any>(null);
  const playerPositionsRef = useRef<PlayerPosition[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const animationFrameRef = useRef<number>();
  const communityCardsRef = useRef<CommunityCards>({
    flop: [],
    turn: null,
    river: null
  });
  const potRef = useRef<THREE.Group>(new THREE.Group());
  const animationMixerRef = useRef<THREE.AnimationMixer | null>(null);
  const actionTextRef = useRef<ActionText[]>([]);
  const [summary, setSummary] = useState('');
  
  // Update the player positions setup with poker positions
  const POKER_POSITIONS = [
    { name: 'BTN', displayName: 'Button' },
    { name: 'SB', displayName: 'Small Blind' },
    { name: 'BB', displayName: 'Big Blind' },
    { name: 'UTG', displayName: 'Under the Gun' },
    { name: 'UTG+1', displayName: 'UTG+1' },
    { name: 'MP', displayName: 'Middle Position' },
    { name: 'LJ', displayName: 'Lojack' },
    { name: 'HJ', displayName: 'Hijack' },
    { name: 'CO', displayName: 'Cutoff' }
  ];

  // Add these constants near the top of the component
  const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
  const SUITS = ['hearts', 'diamonds', 'clubs', 'spades'];

  useEffect(() => {
    if (!mountRef.current) return;

    // Add CCapture script to the document
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/ccapture.js@1.1.0/build/CCapture.all.min.js';
    script.async = true;
    script.onload = () => {
      console.log('CCapture loaded successfully');
    };
    script.onerror = () => {
      console.error('Failed to load CCapture');
    };
    document.body.appendChild(script);

    // Setup scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.background = new THREE.Color(0x1a5f2d);

    // Setup camera to be directly overhead
    const camera = new THREE.PerspectiveCamera(
      45,  // Back to original FOV
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    cameraRef.current = camera;
    camera.position.set(0, 45, 0); // Back to original overhead position
    camera.lookAt(0, 0, 0);

    // Setup renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    rendererRef.current = renderer;
    renderer.setSize(window.innerWidth, window.innerHeight);
    mountRef.current.appendChild(renderer.domElement);

    // Create poker table with a more complex shape
    const tableGroup = new THREE.Group();
    tableGroup.userData.isTable = true; // Mark as table

    // Main felt surface
    const feltGeometry = new THREE.CircleGeometry(12, 32);
    const feltMaterial = new THREE.MeshPhongMaterial({ 
      color: 0x1a5f2d,
      specular: 0x222222,
      shininess: 10
    });
    const felt = new THREE.Mesh(feltGeometry, feltMaterial);
    felt.rotation.x = -Math.PI / 2;
    felt.position.y = 0;
    tableGroup.add(felt);

    // Table rim
    const rimGeometry = new THREE.TorusGeometry(12, 0.4, 16, 100);
    const rimMaterial = new THREE.MeshPhongMaterial({ 
      color: 0x4a2810,
      specular: 0x222222,
      shininess: 30
    });
    const rim = new THREE.Mesh(rimGeometry, rimMaterial);
    rim.userData.isTableRim = true; // Mark as table rim
    rim.rotation.x = Math.PI / 2;
    rim.position.y = 0.2;
    tableGroup.add(rim);

    scene.add(tableGroup);

    // Add lighting
    const frontLight = new THREE.DirectionalLight(0xffffff, 2);
    frontLight.position.set(0, 15, 15);
    scene.add(frontLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 1);
    backLight.position.set(0, 15, -15);
    scene.add(backLight);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);

    // Update player positions with poker positions
    playerPositionsRef.current = POKER_POSITIONS.map((pos, i) => {
      const angle = ((i * Math.PI * 2) / 9) + Math.PI;
      const radius = 13; // Increased radius to place outside table
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      const position = new THREE.Vector3(x, 0, z);

      // Create player position marker
      const markerGeometry = new THREE.CircleGeometry(0.8, 32);
      const markerMaterial = new THREE.MeshPhongMaterial({ color: 0x333333 });
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      marker.position.copy(position);
      marker.rotation.x = -Math.PI / 2;
      scene.add(marker);

      // Create position name tag
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = 256;
      canvas.height = 64;
      context.fillStyle = '#ffffff';
      context.font = 'bold 32px Arial';
      context.textAlign = 'center';
      context.fillText(pos.name, 128, 40);
      
      const texture = new THREE.CanvasTexture(canvas);
      const nameMaterial = new THREE.SpriteMaterial({ map: texture });
      const nameSprite = new THREE.Sprite(nameMaterial);
      nameSprite.position.copy(position);
      nameSprite.position.y = 0.5;
      nameSprite.scale.set(2, 0.5, 1);
      scene.add(nameSprite);

      return {
        position,
        tablePosition: pos.name as Position,
        name: pos.name,
        displayName: pos.displayName,
        cards: [],
        chipStack: new THREE.Group(),
        isActive: false
      };
    });

    // Add pot to scene
    scene.add(potRef.current);

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      renderer.render(scene, camera);
      
      if (capturer.current && capturer.current.capturing) {
        capturer.current.capture(renderer.domElement);
      }
    };
    animate();

    // Add this inside useEffect, after renderer setup
    const handleResize = () => {
      if (!rendererRef.current || !cameraRef.current) return;
      
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      document.body.removeChild(script);
      window.removeEventListener('resize', handleResize);
      
      // Clean up any remaining action texts
      actionTextRef.current.forEach(text => {
        if (sceneRef.current) {
          sceneRef.current.remove(text.mesh);
        }
      });
      actionTextRef.current = [];
    };
  }, []);

  const startRecording = () => {
    const CCapture = window.CCapture;
    if (typeof CCapture === 'function') {
      capturer.current = new CCapture({
        format: 'webm',
        framerate: 60,
      });
      capturer.current.start();
    } else {
      console.error('CCapture is not loaded');
    }
  };

  const stopRecording = () => {
    if (capturer.current) {
      capturer.current.stop();
      capturer.current.save();
    }
  };

  // Update the sample hand with more detailed actions
  const sampleHand = [
    // Pre-flop action
    { player: 'SB', type: 'bet', amount: 5 }, // Small blind
    { player: 'BB', type: 'bet', amount: 10 }, // Big blind
    { player: 'UTG', type: 'raise', amount: 100 }, // UTG raises to 100
    { player: 'UTG+1', type: 'fold' },
    { player: 'MP', type: 'fold' },
    { player: 'MP+1', type: 'fold' },
    { player: 'HJ', type: 'fold' },
    { player: 'CO', type: 'fold' },
    { player: 'BTN', type: 'fold' },
    { player: 'SB', type: 'fold' },
    { player: 'BB', type: 'fold' },
    // Hand ends, UTG wins uncontested
  ];

  // Add this utility function
  const createCardTexture = (rank: string, suit: string): THREE.Texture => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 712; // Make canvas taller for better card proportions
    const ctx = canvas.getContext('2d')!;

    // Draw card background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 512, 712);

    // Draw card border
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 16;
    ctx.strokeRect(8, 8, 496, 696);

    // Set text properties
    ctx.fillStyle = ['hearts', 'diamonds'].includes(suit) ? '#ff0000' : '#000000';
    ctx.font = 'bold 200px Arial'; // Make text larger
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Draw rank in corners
    ctx.font = 'bold 80px Arial';
    ctx.fillText(rank, 50, 60); // Top left
    ctx.fillText(rank, 462, 652); // Bottom right (rotated)

    // Draw large central rank
    ctx.font = 'bold 200px Arial';
    ctx.fillText(rank, 256, 200);

    // Draw large central suit
    ctx.font = 'bold 300px Arial';
    const suitSymbol = {
      'hearts': '♥',
      'diamonds': '♦',
      'clubs': '♣',
      'spades': '♠'
    }[suit];
    ctx.fillText(suitSymbol!, 256, 450);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  };

  const createCardBackTexture = (): THREE.Texture => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 712;
    const ctx = canvas.getContext('2d')!;

    // Draw background
    ctx.fillStyle = '#0066cc';
    ctx.fillRect(0, 0, 512, 712);

    // Draw pattern
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    
    // Draw diagonal lines
    for (let i = -712; i < 712; i += 20) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(512, i + 512);
      ctx.stroke();
    }

    // Add border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 16;
    ctx.strokeRect(8, 8, 496, 696);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  };

  const createCard = (rank: string, suit: string, faceUp: boolean): THREE.Group => {
    const cardGroup = new THREE.Group();
    cardGroup.userData.isCard = true; // Mark as card for cleanup
    const cardGeometry = new THREE.PlaneGeometry(1.5, 2.1);

    let frontTexture, backTexture;
    if (faceUp) {
      // Create front texture with the specific card
      frontTexture = createCardTexture(rank, suit);
      frontTexture.colorSpace = THREE.SRGBColorSpace;
      
      const frontMaterial = new THREE.MeshStandardMaterial({ 
        map: frontTexture,
        side: THREE.FrontSide,
      });
      
      const frontCard = new THREE.Mesh(cardGeometry, frontMaterial);
      cardGroup.add(frontCard);

      // Add back side to prevent seeing through the card
      backTexture = createCardBackTexture();
      backTexture.colorSpace = THREE.SRGBColorSpace;
      
      const backMaterial = new THREE.MeshStandardMaterial({ 
        map: backTexture,
        side: THREE.BackSide,
      });
      
      const backCard = new THREE.Mesh(cardGeometry, backMaterial);
      backCard.position.z = -0.001; // Slight offset to prevent z-fighting
      cardGroup.add(backCard);
    } else {
      // For face down cards, use back texture on both sides
      backTexture = createCardBackTexture();
      backTexture.colorSpace = THREE.SRGBColorSpace;
      
      const backMaterial = new THREE.MeshStandardMaterial({ 
        map: backTexture,
        side: THREE.DoubleSide,
      });
      
      const card = new THREE.Mesh(cardGeometry, backMaterial);
      cardGroup.add(card);
    }

    return cardGroup;
  };

  const createChipStack = (amount: number, position: THREE.Vector3) => {
    const group = new THREE.Group();
    group.userData.isChip = true; // Mark as chip for cleanup
    const chipGeometry = new THREE.CylinderGeometry(0.4, 0.4, 0.1, 32);
    
    // Create different colored materials based on chip values
    const chipMaterials: Record<1 | 5 | 25 | 100, THREE.MeshPhongMaterial> = {
      1: new THREE.MeshPhongMaterial({ color: 0xffffff }), // white - $1
      5: new THREE.MeshPhongMaterial({ color: 0xff0000 }), // red - $5
      25: new THREE.MeshPhongMaterial({ color: 0x00ff00 }), // green - $25
      100: new THREE.MeshPhongMaterial({ color: 0x000000 }) // black - $100
    };

    // Calculate number of each chip type
    let remaining = amount;
    const chipCounts = {
      100: Math.floor(remaining / 100),
      25: Math.floor((remaining % 100) / 25),
      5: Math.floor((remaining % 25) / 5),
      1: Math.floor(remaining % 5)
    };

    let heightOffset = 0;
    
    // Create chips of each denomination
    Object.entries(chipCounts).reverse().forEach(([value, count]) => {
      const numValue = parseInt(value) as 1 | 5 | 25 | 100;
      for (let i = 0; i < count; i++) {
        const chip = new THREE.Mesh(chipGeometry, chipMaterials[numValue]);
        chip.position.copy(position);
        chip.position.y += heightOffset;
        group.add(chip);
        heightOffset += 0.1; // Stack chips vertically
      }
    });

    // If no chips were created (amount was 0), create a single transparent chip
    if (group.children.length === 0) {
      const emptyChip = new THREE.Mesh(
        chipGeometry,
        new THREE.MeshPhongMaterial({ 
          color: 0xffffff,
          transparent: true,
          opacity: 0.3 
        })
      );
      emptyChip.position.copy(position);
      group.add(emptyChip);
    }

    return group;
  };

  const createDeck = () => {
    const deck: CardData[] = [];
    // Make card geometry simpler and larger
    const cardGeometry = new THREE.PlaneGeometry(1.5, 2.1); // 2.1 maintains card proportions (1.5 * 1.4)
    const deckPosition = new THREE.Vector3(-3, 0.1, -3);
    
    for (const rank of RANKS) {
      for (const suit of SUITS) {
        // Create front texture
        const frontTexture = createCardTexture(rank, suit);
        frontTexture.colorSpace = THREE.SRGBColorSpace; // Fix texture color space
        
        // Create back texture
        const backTexture = createCardBackTexture();
        backTexture.colorSpace = THREE.SRGBColorSpace;

        // Create front and back meshes
        const frontMaterial = new THREE.MeshStandardMaterial({ 
          map: frontTexture,
          side: THREE.FrontSide,
        });
        
        const backMaterial = new THREE.MeshStandardMaterial({ 
          map: backTexture,
          side: THREE.BackSide,
        });

        // Create two planes for front and back
        const frontCard = new THREE.Mesh(cardGeometry, frontMaterial);
        const backCard = new THREE.Mesh(cardGeometry, backMaterial);
        
        // Offset back slightly to prevent z-fighting
        backCard.position.z = -0.001;

        // Group the front and back together
        const cardGroup = new THREE.Group();
        cardGroup.add(frontCard);
        cardGroup.add(backCard);
        
        cardGroup.position.copy(deckPosition);

        deck.push({
          rank,
          suit,
          mesh: cardGroup,
          targetPosition: cardGroup.position.clone(),
          targetRotation: cardGroup.rotation.clone()
        });
      }
    }
    return deck;
  };

  const dealCards = async (scene: THREE.Scene, playerPositions: PlayerPosition[], heroCards?: string[]) => {
    // Create and add deck to scene
    const deck = createDeck();
    deck.forEach(card => scene.add(card.mesh));

    for (let round = 0; round < 2; round++) {
      for (let i = 0; i < playerPositions.length; i++) {
        const player = playerPositions[i];
        
        // Skip inactive players
        if (!player.isActive) continue;

        // For hero, use the specific cards from summary
        let cardGroup;
        if (player.name === 'Hero' && heroCards) {
          const cardCode = heroCards[round];
          const rank = cardCode[0];
          const suit = {
            'h': 'hearts',
            'd': 'diamonds',
            'c': 'clubs',
            's': 'spades'
          }[cardCode[1]] || 'hearts';
          
          cardGroup = createCard(rank, suit, true); // true means face up
        } else {
          cardGroup = createCard('', '', false); // face down card for non-hero players
        }

        // Calculate position
        const angle = Math.atan2(player.position.z, player.position.x);
        const radius = 10;
        const cardSpacing = 1.5;
        const cardX = Math.cos(angle) * radius;
        const cardZ = Math.sin(angle) * radius;
        const offsetX = (round - 0.5) * cardSpacing;
        
        const targetPosition = new THREE.Vector3(
          cardX + offsetX,
          0.05,
          cardZ
        );

        scene.add(cardGroup);
        await animateCard({
          rank: '',
          suit: '',
          mesh: cardGroup,
          targetPosition,
          targetRotation: new THREE.Euler(-Math.PI / 2, 0, 0)
        });
        player.cards.push(cardGroup);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const animateCard = (card: CardData): Promise<void> => {
    return new Promise((resolve) => {
      const duration = 500;
      const startPos = card.mesh.position.clone();
      const startRot = card.mesh.rotation.clone();
      const startTime = Date.now();

      function update() {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Use easing function for smooth animation
        const eased = 1 - Math.pow(1 - progress, 3);

        card.mesh.position.lerpVectors(startPos, card.targetPosition, eased);
        card.mesh.rotation.x = startRot.x + (card.targetRotation.x - startRot.x) * eased;
        card.mesh.rotation.y = startRot.y + (card.targetRotation.y - startRot.y) * eased;
        card.mesh.rotation.z = startRot.z + (card.targetRotation.z - startRot.z) * eased;

        if (progress < 1) {
          requestAnimationFrame(update);
        } else {
          resolve();
        }
      }
      update();
    });
  };

  const dealCommunityCards = async (scene: THREE.Scene, newCards: string[]) => {
    if (!scene) return;
    
    // Use consistent spacing for all community cards
    const cardSpacing = 1.5;
    // Calculate start position to center all 5 cards
    const startX = -(cardSpacing * 2); // For 5 cards total
    
    // For flop, start at beginning. For turn/river, start after existing cards
    const nextPosition = communityCardsRef.current.flop.length + 
                        (communityCardsRef.current.turn ? 1 : 0);

    for (let i = 0; i < newCards.length; i++) {
      const cardCode = newCards[i];
      const rank = cardCode[0];
      const suit = {
        'h': 'hearts',
        'd': 'diamonds',
        'c': 'clubs',
        's': 'spades'
      }[cardCode[1]] || 'hearts';

      const cardGroup = createCard(rank, suit, true);
      cardGroup.userData.isCommunityCard = true;
      
      // Simply place each new card next to the previous ones
      const x = startX + (nextPosition + i) * cardSpacing;
      const targetPosition = new THREE.Vector3(x, 0.05, 0);
      
      scene.add(cardGroup);
      await animateCard({
        rank,
        suit,
        mesh: cardGroup,
        targetPosition,
        targetRotation: new THREE.Euler(-Math.PI / 2, 0, 0)
      });

      // Store card reference
      const cardData = { 
        rank, 
        suit, 
        mesh: cardGroup, 
        targetPosition, 
        targetRotation: new THREE.Euler(-Math.PI / 2, 0, 0) 
      };

      if (newCards.length === 3) {
        communityCardsRef.current.flop.push(cardData);
      } else if (newCards.length === 1) {
        if (!communityCardsRef.current.turn) {
          communityCardsRef.current.turn = cardData;
        } else {
          communityCardsRef.current.river = cardData;
        }
      }
    }
  };

  // Update showActionText to include more poker-specific formatting
  const showActionText = (text: string, position: THREE.Vector3) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    canvas.width = 512;
    canvas.height = 128;

    // Draw text with poker styling
    context.fillStyle = '#000000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#ffd700'; // Gold border
    context.lineWidth = 4;
    context.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
    context.fillStyle = '#ffffff';
    context.font = 'bold 48px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture });
    const sprite = new THREE.Sprite(material);
    
    // Position above the player
    sprite.position.copy(position);
    sprite.position.y += 2;
    sprite.scale.set(4, 1, 1);

    if (sceneRef.current) {
      sceneRef.current.add(sprite);
    }

    // Add to action texts array
    actionTextRef.current.push({
      mesh: sprite,
      startTime: Date.now(),
      duration: 2000 // Show for 2 seconds
    });

    // Start fade out animation
    const animate = () => {
      const now = Date.now();
      actionTextRef.current = actionTextRef.current.filter(text => {
        const elapsed = now - text.startTime;
        if (elapsed >= text.duration) {
          if (sceneRef.current) {
            sceneRef.current.remove(text.mesh);
          }
          return false;
        }
        
        // Fade out during last second
        if (elapsed > text.duration - 1000) {
          const opacity = 1 - (elapsed - (text.duration - 1000)) / 1000;
          text.mesh.material.opacity = opacity;
        }
        
        // Float upward
        text.mesh.position.y += 0.01;
        
        return true;
      });

      if (actionTextRef.current.length > 0) {
        requestAnimationFrame(animate);
      }
    };
    animate();
  };

  // Update handleBet to show more detailed bet information
  const handleBet = async (playerName: string, amount: number) => {
    const player = playerPositionsRef.current.find(p => p.name === playerName);
    if (!player || !sceneRef.current) return;

    showActionText(`${playerName} (${player.tablePosition}) bets $${amount}`, player.position);

    const chipStack = createChipStack(amount, player.position);
    const targetPos = new THREE.Vector3(0, 0.1, 0); // Center of table
    
    sceneRef.current.add(chipStack);
    
    // Animate chips moving to pot
    await new Promise<void>((resolve) => {
      const duration = 500;
      const startPos = chipStack.position.clone();
      const startTime = Date.now();

      function update() {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        chipStack.position.lerpVectors(startPos, targetPos, eased);

        if (progress < 1) {
          requestAnimationFrame(update);
        } else {
          potRef.current.add(chipStack);
          resolve();
        }
      }
      update();
    });
  };

  const handleFold = async (playerName: string) => {
    const player = playerPositionsRef.current.find(p => p.name === playerName);
    if (!player) return;

    showActionText(`${playerName} (${player.tablePosition}) folds`, player.position);

    // Animate cards flying away
    const promises = player.cards.map(card => {
      return new Promise<void>((resolve) => {
        const duration = 500;
        const startPos = card.position.clone();
        const startRot = card.rotation.clone();
        const targetPos = startPos.clone().add(new THREE.Vector3(0, 5, -5));
        const targetRot = new THREE.Euler(-Math.PI, 0, 0);
        const startTime = Date.now();

        function update() {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);

          card.position.lerpVectors(startPos, targetPos, eased);
          card.rotation.x = startRot.x + (targetRot.x - startRot.x) * eased;

          if (progress < 1) {
            requestAnimationFrame(update);
          } else {
            if (sceneRef.current) {
              sceneRef.current.remove(card);
            }
            resolve();
          }
        }
        update();
      });
    });

    await Promise.all(promises);
    player.cards = [];
  };

  // Update player position markers to be more visible
  const createPlayerMarker = (position: THREE.Vector3, name: string, isActive: boolean = false) => {
    // Create a group to hold marker and text
    const group = new THREE.Group();

    // Create larger, more visible marker
    const markerGeometry = new THREE.CircleGeometry(1.2, 32);
    const markerMaterial = new THREE.MeshPhongMaterial({ 
      color: 0x333333,
      opacity: 0.9,
      transparent: true 
    });
    const marker = new THREE.Mesh(markerGeometry, markerMaterial);
    marker.position.copy(position);
    marker.rotation.x = -Math.PI / 2;
    group.add(marker);

    // Create clearer position text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    canvas.width = 512;
    canvas.height = 128;

    // Draw background
    context.fillStyle = '#000000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw border
    context.strokeStyle = '#ffffff';
    context.lineWidth = 4;
    context.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);

    // Draw text
    context.fillStyle = '#ffffff';
    context.font = 'bold 72px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(name, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const textMaterial = new THREE.SpriteMaterial({ map: texture });
    const textSprite = new THREE.Sprite(textMaterial);
    textSprite.position.copy(position);
    textSprite.position.y = 0.5;
    textSprite.scale.set(3, 0.8, 1);
    group.add(textSprite);

    return group;
  };

  // Update showStreetHeading to keep the text visible but with original camera angle
  const showStreetHeading = (text: string) => {
    if (!sceneRef.current) return;

    // Remove any existing street heading
    const existingHeading = sceneRef.current.children.find(
      child => child instanceof THREE.Sprite && child.userData.isStreetHeading
    );
    if (existingHeading) {
      sceneRef.current.remove(existingHeading);
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    canvas.width = 512;  // Smaller canvas
    canvas.height = 128; // More compact height

    // Draw heading with a more subtle style
    context.fillStyle = 'rgba(0, 0, 0, 0.7)'; // Semi-transparent background
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#ffd700';
    context.lineWidth = 4; // Thinner border
    context.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
    context.fillStyle = '#ffffff';
    context.font = 'bold 72px Arial'; // Smaller font
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true,
      depthTest: false
    });
    const sprite = new THREE.Sprite(material);
    
    // Position above the table in the green space
    sprite.position.set(0, 15, -8); // Lower Y, moved forward in Z
    sprite.scale.set(6, 1.5, 1); // Smaller scale
    sprite.userData.isStreetHeading = true;

    sceneRef.current.add(sprite);
    return sprite;
  };

  // Add this parsing function
  const parseHandSummary = (summary: string): ParsedHand => {
    console.log("Parsing summary:", summary);
    const parts = summary.split(' | ');
    
    // Parse hero cards
    const heroMatch = parts[0].match(/Hero \((.*?)\): (..)(..)/);
    if (!heroMatch) {
      console.error("Failed to parse hero cards");
      throw new Error("Invalid hand summary format");
    }
    const heroCards = [heroMatch[2], heroMatch[3]];
    const heroPosition = heroMatch[1];

    // Parse board with better empty handling
    const boardMatch = parts[1].match(/Board: (.*)/);
    console.log("Board part:", parts[1]);
    console.log("Board match:", boardMatch);
    
    let board: string[] = [];
    if (boardMatch && boardMatch[1].trim()) {
      board = boardMatch[1].trim().match(/.{2}/g) || [];
    }
    console.log("Parsed board:", board);

    // Parse actions by street
    const actions: ParsedHand['actions'] = [];
    const players = new Set<string>();
    
    ['preflop', 'flop', 'turn', 'river'].forEach((street, i) => {
      if (parts[i + 2] && parts[i + 2].startsWith(street + ':')) {
        const streetActions = parts[i + 2].substring(street.length + 2).trim().split(', ');
        
        console.log(`Parsing ${street} actions:`, streetActions);
        
        streetActions.forEach(action => {
          // Match format: [1]Hero (UTG) bet $22
          const actionMatch = action.match(/\[(\d+)\](.*?) \((.*?)\) (bet|call|fold|check|raise)(?: \$(\d+))?/i);
          if (actionMatch) {
            const [_, actionNumber, name, position, actionType, amountStr] = actionMatch;
            players.add(`${name}|${position}`);
            
            // Convert action types to simulation format
            const type = actionType.toLowerCase();
            const simulationType = type === 'bet' ? 'bets' : 
                                 type === 'raise' ? 'raises to' :
                                 type === 'call' ? 'calls' :
                                 type === 'fold' ? 'fold' :
                                 'check';

            actions.push({
              street: street as 'preflop' | 'flop' | 'turn' | 'river',
              player: name,
              position,
              type: simulationType,
              amount: amountStr ? parseInt(amountStr) : undefined,
              actionNumber: parseInt(actionNumber)
            });
          } else {
            console.error(`Failed to parse action: ${action}`);
          }
        });
      }
    });

    console.log("Parsed actions:", actions);

    // Parse pot
    const potMatch = parts[parts.length - 1].match(/Final pot: \$(\d+)/);
    const pot = potMatch ? parseInt(potMatch[1]) : 0;

    // Convert players set to array
    const playersList = Array.from(players).map(p => {
      const [name, position] = p.split('|');
      return { name, position };
    });

    const result = {
      heroCards,
      board,
      actions,
      players: playersList,
      pot
    };

    console.log("Final parsed hand:", result);
    return result;
  };

  // Update simulateFromSummary to handle preflop actions correctly
  const simulateFromSummary = async (summary: string) => {
    if (!sceneRef.current) return;
    setIsPlaying(true);

    try {
      const parsedHand = parseHandSummary(summary);
      
      // Clear only cards and chips, keep player positions and table
      if (sceneRef.current) {
        // Remove only cards and chips
        sceneRef.current.children = sceneRef.current.children.filter(child => {
          const isCard = child instanceof THREE.Group && child.userData.isCard;
          const isChip = child instanceof THREE.Group && child.userData.isChip;
          return !(isCard || isChip);
        });
        potRef.current.clear();
      }

      // Just update existing players' states - don't recreate positions
      playerPositionsRef.current.forEach(player => {
        const matchingPlayer = parsedHand.players.find(p => p.position === player.tablePosition);
        if (matchingPlayer) {
          player.name = matchingPlayer.name;
          player.isActive = true;
        } else {
          player.isActive = false;
        }
        player.cards = []; // Clear any existing cards
      });

      // Deal hole cards first
      const preflopHeading = showStreetHeading("PRE-FLOP");
      await dealCards(sceneRef.current, playerPositionsRef.current, parsedHand.heroCards);
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Play each street's actions
      for (const street of ['preflop', 'flop', 'turn', 'river'] as const) {
        const streetActions = parsedHand.actions.filter(a => a.street === street);
        
        if (street !== 'preflop') {
          // Remove previous street heading
          if (preflopHeading) {
            sceneRef.current.remove(preflopHeading);
          }
          
          const heading = showStreetHeading(street.toUpperCase());
          await new Promise(resolve => setTimeout(resolve, 1500));

          if (street === 'flop') {
            await dealCommunityCards(sceneRef.current, parsedHand.board.slice(0, 3));
          } else if (street === 'turn') {
            await dealCommunityCards(sceneRef.current, [parsedHand.board[3]]);
          } else if (street === 'river') {
            await dealCommunityCards(sceneRef.current, [parsedHand.board[4]]);
          }
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        // Play actions in order
        for (const action of streetActions) {
          if (action.type === 'bets' || action.type === 'raises to') {
            await handleBet(action.player, action.amount!);
          } else if (action.type === 'calls') {
            await handleBet(action.player, action.amount!);
          } else if (action.type === 'fold') {
            await handleFold(action.player);
          } else if (action.type === 'check') {
            const player = playerPositionsRef.current.find(p => p.name === action.player);
            if (player) {
              showActionText(`${action.player} (${player.tablePosition}) checks`, player.position);
            }
          }
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }

    } catch (error) {
      console.error('Error simulating hand:', error);
    } finally {
      setIsPlaying(false);
    }
  };

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', backgroundColor: '#000' }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        padding: '15px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        zIndex: 1000
      }}>
        <textarea 
          placeholder="Paste hand summary here..."
          rows={4}
          style={{
            width: '300px',
            padding: '8px',
            borderRadius: '4px'
          }}
          onChange={(e) => setSummary(e.target.value)}
        />
        <button 
          onClick={() => simulateFromSummary(summary)}
          disabled={isPlaying || !summary}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: isPlaying || !summary ? 'default' : 'pointer',
            opacity: isPlaying || !summary ? 0.7 : 1,
            fontWeight: 'bold'
          }}
        >
          {isPlaying ? 'Simulating...' : 'Simulate Hand'}
        </button>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={startRecording}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Start Recording
          </button>
          <button 
            onClick={stopRecording}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Stop & Export
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodexPage;